import Box, { BoxProps } from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import { CheckMark, FormCheck, FormCheckLabel } from 'components/RadioButtonWithLabel';
import { FieldInputProps, FormikProps } from 'formik';
import React, {
  ChangeEvent,
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from 'react';
import { useClickAway } from 'react-use';

export type Props = {
  editable: boolean;
  text: string;
  orientation: 'row' | 'column';
  children?: ReactNode;
  name: string;
  value: string | number;
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  valueChecked: string | number;
  disabled?: boolean;
  field: FieldInputProps<any>;
  displayOptional?: boolean;
  handleChange?: () => void;
  form?: FormikProps<any>;
  wrapperBtnProps?: BoxProps;
};

type ContextType = {
  valueChecked: boolean;
  focus: boolean;
  checked: string | number;
  setFocus: Dispatch<SetStateAction<boolean>>;
  hasSelected: boolean;
};

const Context = createContext({} as ContextType);

const RadioButtonWithOptionalContent: React.VFC<Props> = ({
  editable,
  text,
  orientation = 'row',
  children,
  disabled,
  value,
  field,
  onChange,
  wrapperBtnProps,
}) => {
  const [focus, setFocus] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);
  const disabledClass = !editable && !disabled ? 'is-disabled' : '';
  const ref = useRef(null);

  useClickAway(ref, () => {
    setFocus(false);
  });

  return (
    <Box
      sx={{
        display: {
          xs: 'block',
          sm: 'flex',
        },
        flexDirection: orientation,
        width: '100%',
        py: 1.25,
      }}
    >
      <Box {...wrapperBtnProps}>
        <FormCheck className="form-check" ref={ref}>
          <FormCheckLabel className="form-check-label">
            <input
              type="radio"
              className={`form-check-input ${disabledClass}`}
              disabled={!editable || disabled}
              {...field}
              value={value}
              onChange={(e) => {
                onChange && onChange(e);
                field.onChange(e);
                setHasSelected(true);
                setTimeout(() => {
                  setFocus(true);
                }, 400);
              }}
              checked={field.value === value}
            />
            <CheckMark className="checkmark"></CheckMark>
            <span className="text-muted">{text}</span>
          </FormCheckLabel>
        </FormCheck>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          mt: field?.value === value && orientation === 'column' ? 2 : 0,
        }}
      >
        <Collapse in={field?.value === value}>
          <Context.Provider
            value={{
              valueChecked: field.value === value,
              focus,
              setFocus,
              checked: field.value,
              hasSelected,
            }}
          >
            {children}
          </Context.Provider>
        </Collapse>
      </Box>
    </Box>
  );
};

export const useContextRadioButtonWithOptionalContent = () => {
  return useContext(Context);
};

export default RadioButtonWithOptionalContent;
